import type {
	Resource,
	ResourceSnapshot,
	Signal,
	StateKey,
	TransferState,
} from '@angular/core';
import { linkedSignal, resourceFromSnapshots } from '@angular/core';

export function withTransferredValue<T>(
	input: Resource<T>,
	stateKey: Signal<StateKey<T> | undefined>,
	transferState: TransferState,
): Resource<T> {
	const derived = linkedSignal<
		{ snapshot: ResourceSnapshot<T>; key: StateKey<T> | undefined },
		ResourceSnapshot<T>
	>({
		source: () => ({ snapshot: input.snapshot(), key: stateKey() }),
		computation: ({ snapshot, key }, previous) => {
			// We intentionally don't remove the key from the transfer state after reading, to avoid refetching data during the same session.
			if (
				snapshot.status === 'idle' &&
				key &&
				transferState.hasKey(key)
			) {
				return {
					status: 'resolved' as const,
					value: transferState.get<T>(key, snapshot.value),
				};
			}
			if (
				snapshot.status === 'loading' &&
				previous &&
				previous.value.status !== 'error'
			) {
				// Keep the last good value visible while the new request loads
				return {
					status: 'loading' as const,
					value: previous.value.value,
				};
			}
			return snapshot;
		},
	});
	return resourceFromSnapshots(derived);
}
